import React, { useEffect, useState, useMemo } from 'react';
import { event } from '../../../../types/cms-types';
import styles from './TimeTableGrid.module.scss';

interface TimeTableGridProps {
  events: event[];
  onEventClick: (event: event) => void;
  activeTab: '11/2' | '11/3'; // 現在表示中の日付
}

interface TimeRange {
  start: number; // 時刻（例: 10 = 10:00）
  end: number; // 時刻（例: 12 = 12:00）
}

interface GridEvent extends event {
  timeRange: TimeRange;
  stages: (
    | 'セントラルステージ'
    | '片柳記念ホール'
    | '3号館エントランス'
    | '3号館BIF片柳記念ホール'
    | '3号館南側（鏡回し）'
    | '2号館日本工学院アリーナ'
    | '2号館Alchemy stage (A-stage)'
  )[];
  displayTime?: string; // 表示用の時刻文字列
}

// 時刻文字列をパースする（"10:00~12:00" → {start: 10, end: 12}）
const parseTimeRange = (timeString: string): TimeRange | null => {
  const match = timeString.match(/(\d{1,2}):(\d{2})\s*[~〜～-]\s*(\d{1,2}):(\d{2})/);
  if (!match) return null;

  const startHour = parseInt(match[1], 10);
  const startMin = parseInt(match[2], 10);
  const endHour = parseInt(match[3], 10);
  const endMin = parseInt(match[4], 10);

  return {
    start: startHour + startMin / 60,
    end: endHour + endMin / 60,
  };
};

// ステージ名を短縮表示用に変換
const getStageName = (stage: string): string => {
  const stageMap: { [key: string]: string } = {
    セントラルステージ: 'セントラルステージ',
    片柳記念ホール: '片柳記念ホール',
    '3号館エントランス': '3号館エントランス',
    '3号館BIF片柳記念ホール': '3号館BIF片柳記念ホール',
    '3号館南側（鏡回し）': '3号館南側（鏡回し）',
    '2号館日本工学院アリーナ': '2号館日本工学院アリーナ',
    '2号館Alchemy stage (A-stage)': '2号館Alchemy stage',
  };
  return stageMap[stage] || stage;
};

// ステージごとの背景色
const getStageColor = (index: number): string => {
  const colors = ['#4D83FE', '#B088F9', '#F6B8DF'];
  return colors[index % colors.length];
};

const TimeTableGrid: React.FC<TimeTableGridProps> = ({ events, onEventClick, activeTab }) => {
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [isEventDate, setIsEventDate] = useState<boolean>(false);

  // 全ステージを抽出（重複なし）
  const allStages = useMemo(() => {
    const stagesSet = new Set<
      | 'セントラルステージ'
      | '片柳記念ホール'
      | '3号館エントランス'
      | '3号館BIF片柳記念ホール'
      | '3号館南側（鏡回し）'
      | '2号館日本工学院アリーナ'
      | '2号館Alchemy stage (A-stage)'
    >();
    events.forEach((event) => {
      if (event.event_table) {
        event.event_table.forEach((stage) => stagesSet.add(stage));
      }
    });
    return Array.from(stagesSet);
  }, [events]);

  // ISO8601日時文字列から日付と時刻を抽出（JST変換）
  const parseScheduleItem = (isoString: string): { day: '11/2' | '11/3'; time: string } | null => {
    const date = new Date(isoString);

    // JST（UTC+9）に変換
    const jstOffset = 9 * 60; // 分単位
    const utcTime = date.getTime();
    const jstTime = new Date(utcTime + jstOffset * 60000);

    const month = jstTime.getUTCMonth() + 1;
    const day = jstTime.getUTCDate();
    const hours = jstTime.getUTCHours();
    const minutes = jstTime.getUTCMinutes();

    // 日付の判定
    const dayString =
      month === 11 && day === 2 ? '11/2' : month === 11 && day === 3 ? '11/3' : null;
    if (!dayString) return null;

    // 時刻をフォーマット
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

    return { day: dayString, time: timeString };
  };

  // イベントを解析してグリッド用データに変換
  const gridEvents: GridEvent[] = useMemo(() => {
    const result: GridEvent[] = [];

    events.forEach((event) => {
      // 新しいevent_scheduleを優先的に処理
      if (event.event_schedule && event.event_schedule.length > 0) {
        event.event_schedule.forEach((schedule) => {
          const parsed = parseScheduleItem(schedule.event_day);
          if (!parsed) return;

          // 現在のタブの日付と一致するスケジュールのみ処理
          if (parsed.day !== activeTab) return;

          // event_timeフィールドから終了時刻を取得
          const timeRange = parseTimeRange(schedule.event_time);
          if (!timeRange) return;

          result.push({
            ...event,
            timeRange,
            stages: event.event_table || [],
            displayTime: schedule.event_time, // 各スケジュールの時刻を保持
          });
        });
      }
      // 後方互換性: 旧event_timetableの処理
      else if (event.event_timetable) {
        const timeRange = parseTimeRange(event.event_timetable);
        if (timeRange) {
          result.push({
            ...event,
            timeRange,
            stages: event.event_table || [],
            displayTime: event.event_timetable,
          });
        }
      }
    });

    return result;
  }, [events, activeTab]);

  // 時間軸の範囲を固定（9:00〜18:00）
  const timeAxis = useMemo(() => {
    const hours: number[] = [];
    // 9:30から17:30まで30分刻み
    for (let h = 9.5; h <= 17.5; h += 0.5) {
      hours.push(h);
    }
    return hours;
  }, []);

  // 現在時刻を更新（日本時間）
  useEffect(() => {
    const updateCurrentTime = () => {
      const now = new Date();
      // 日本時間に変換（UTC+9）
      const jstOffset = 9 * 60; // 分単位
      const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
      const jstTime = new Date(utcTime + jstOffset * 60000);

      const hours = jstTime.getHours();
      const minutes = jstTime.getMinutes();
      setCurrentTime(hours + minutes / 60);

      // 日付チェック（11/2または11/3かどうか）
      const month = jstTime.getMonth() + 1; // 0-indexedなので+1
      const date = jstTime.getDate();
      const year = jstTime.getFullYear();

      // 2025年11月2日または11月3日かどうか
      const isNov2or3 = year === 2025 && month === 11 && (date === 2 || date === 3);
      setIsEventDate(isNov2or3);
    };

    updateCurrentTime();
    const interval = setInterval(updateCurrentTime, 60000); // 1分ごとに更新

    return () => clearInterval(interval);
  }, []);

  // 時刻を位置（%）に変換（9:30〜17:30の範囲）
  const timeToPosition = (time: number): number => {
    const minTime = 9.5; // 9:30
    const maxTime = 17.5; // 17:30
    return ((time - minTime) / (maxTime - minTime)) * 100;
  };

  // イベントの高さを計算（カード間の余白を考慮）
  const getEventHeight = (timeRange: TimeRange): number => {
    const duration = timeRange.end - timeRange.start;
    const totalDuration = 17.5 - 9.5; // 8時間
    const heightPercent = (duration / totalDuration) * 100;

    // カード間の余白分を引く（4pxをパーセントに変換）
    // gridBodyの高さに対する4pxの割合を計算
    const marginInPercent = 0.4; // 約4px相当
    return heightPercent - marginInPercent;
  };

  if (allStages.length === 0 || timeAxis.length === 0) {
    return (
      <div className={styles.noData}>
        <p>タイムテーブルデータがありません</p>
      </div>
    );
  }

  return (
    <div className={styles.timetableGrid}>
      {/* ヘッダー（ステージ名） */}
      <div className={styles.header}>
        <div className={styles.timeAxisHeader}></div>
        {allStages.map((stage, index) => (
          <div
            key={stage}
            className={styles.stageHeader}
            style={{ backgroundColor: getStageColor(index) }}
          >
            {getStageName(stage)}
          </div>
        ))}
      </div>

      {/* グリッド本体 */}
      <div className={styles.gridBody}>
        {/* 時間軸 */}
        <div className={styles.timeAxis}>
          {timeAxis.map((hour, index) => {
            const hours = Math.floor(hour);
            const minutes = (hour % 1) * 60;
            return (
              <div
                key={hour}
                className={styles.timeLabel}
                style={{
                  top: `${(index / (timeAxis.length - 1)) * 100}%`,
                }}
              >
                {`${hours}:${minutes === 0 ? '00' : minutes.toString().padStart(2, '0')}`}
              </div>
            );
          })}
        </div>

        {/* ステージ列 */}
        {allStages.map((stage, stageIndex) => (
          <div key={stage} className={styles.stageColumn}>
            {/* 時間区切り線 */}
            {timeAxis.map((hour, index) => (
              <div
                key={hour}
                className={styles.timeLine}
                style={{
                  top: `${(index / (timeAxis.length - 1)) * 100}%`,
                }}
              ></div>
            ))}

            {/* イベントカード */}
            {gridEvents
              .filter((event) => event.stages.includes(stage))
              .map((event) => {
                const duration = event.timeRange.end - event.timeRange.start;
                const isLargeCard = duration >= 1; // 1時間以上

                return (
                  <div
                    key={`${event.id}-${event.timeRange.start}`}
                    className={styles.eventCard}
                    data-height={isLargeCard ? 'large' : 'small'}
                    style={{
                      top: `${timeToPosition(event.timeRange.start)}%`,
                      height: `${getEventHeight(event.timeRange)}%`,
                      backgroundColor: getStageColor(stageIndex),
                    }}
                    onClick={() => onEventClick(event)}
                  >
                    <div className={styles.eventTime}>
                      {event.displayTime?.replace(/[~〜～]/g, '〜')}
                    </div>
                    <div className={styles.eventTitle}>{event.event_title}</div>
                  </div>
                );
              })}
          </div>
        ))}

        {/* 現在時刻ライン */}
        {isEventDate && currentTime >= 9.5 && currentTime <= 17.5 && (
          <div
            className={styles.currentTimeLine}
            style={{
              top: `${timeToPosition(currentTime)}%`,
            }}
          >
            <div className={styles.currentTimeLabel}>
              現在
              {` ${Math.floor(currentTime)}:${String(Math.floor((currentTime % 1) * 60)).padStart(2, '0')}`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeTableGrid;
